import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { formatDistanceToNowStrict } from "date-fns";
import Avatar from "../../components/common/Avatar";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import { useReceivedRequests } from "../../hooks/last-minute/useOpportunityRequests";
import { urgencyMeta } from "../../utils/lastMinute";

const timeAgo = (iso) => {
  try {
    return `${formatDistanceToNowStrict(new Date(iso))} ago`;
  } catch (e) {
    return "";
  }
};

// The fixed "Player Opportunity" intent pill (no dropdown, Brief §3/§16).
const IntentPill = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E9F2FF] border border-[#BDD9FF] px-2.5 py-0.5 text-[11.5px] font-semibold text-[#1466D8]">
    <span className="w-[5px] h-[5px] rounded-full bg-[#1466D8]" />
    Player Opportunity
  </span>
);

const RequestCard = ({ request, onAccept, onIgnore, busy }) => {
  const ctx = request.context_snapshot || {};
  const sender = request.sender || {};
  const accepted = request.status === "accepted";
  // Named listings show the player name (design 9b); anon stays nameless. The name
  // is only present on the snapshot for a non-anonymized listing (BE), so anon can
  // never leak it here.
  const subject = ctx.anonymized
    ? [ctx.position, ctx.age].filter((v) => v !== null && v !== undefined && v !== "").join(", ")
    : ctx.name || ctx.player_name || ctx.position || "Player";
  const contextLine = [urgencyMeta(ctx.urgency_class).label, ctx.age ? `${ctx.age}` : null, ctx.salary_band]
    .filter(Boolean)
    .join(" · ");
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-2xl border border-[#E4E9F1] p-5" style={{ boxShadow: "0 1px 2px rgba(16,24,40,.05)" }}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <IntentPill />
        <span className="text-[11.5px] text-[#98A5B8]">{timeAgo(request.created_at)}</span>
      </div>

      {/* Sender */}
      <div className="flex items-center gap-2 mb-3">
        <Avatar src={null} fallbackText={sender.name} size={28} />
        <div className="min-w-0">
          <p className="text-[13.5px] font-semibold text-[#14263D] truncate">{sender.name || "Agent"}</p>
          {sender.agency && <p className="text-[11.5px] text-[#8A98AC] truncate">{sender.agency}</p>}
        </div>
      </div>

      {/* About this listing */}
      <div className="flex items-center justify-between gap-3 rounded-xl bg-[#F8FAFC] p-3 mb-3">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-[#14263D] truncate">{subject}</p>
          <p className="text-[11.5px] text-[#8A98AC] truncate">{contextLine}</p>
        </div>
        <span className="inline-flex items-center rounded-full bg-[#EDF1F6] px-2.5 py-0.5 text-[11px] text-[#71829A] flex-shrink-0">
          via Last Minute
        </span>
      </div>

      {request.message && <p className="text-[13px] text-[#33475E] leading-snug mb-4">“{request.message}”</p>}

      {/* Actions */}
      {accepted ? (
        <button
          onClick={() => navigate(`/inbox?channel=${request.channel_cid}`)}
          className="w-full min-h-[40px] px-3 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0]"
        >
          Open chat
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onAccept(request)}
            disabled={busy}
            className="flex-1 min-h-[40px] px-3 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0] disabled:opacity-60"
          >
            Accept
          </button>
          <button
            onClick={() => onIgnore(request)}
            disabled={busy}
            className="flex-1 min-h-[40px] px-3 py-2 rounded-lg border border-[#E4E9F1] text-[13px] font-semibold text-[#5A6B82] hover:bg-gray-50 disabled:opacity-60"
          >
            Ignore
          </button>
        </div>
      )}
    </div>
  );
};

const ReceivedRequestsPage = () => {
  const { requests, loading, accept, ignore } = useReceivedRequests();
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const onAccept = async (r) => {
    setBusyId(r.id);
    const res = await accept(r.id);
    setBusyId(null);
    if (res.success && res.data?.channel_cid) {
      navigate(`/inbox?channel=${res.data.channel_cid}`);
    } else if (!res.success) {
      toast.error(res.error || "Could not accept the request.");
    }
  };
  const onIgnore = async (r) => {
    setBusyId(r.id);
    const res = await ignore(r.id);
    setBusyId(null);
    if (!res.success) toast.error(res.error || "Could not ignore the request.");
  };

  return (
    // Platform-standard container (W1-04): no fixed max-width, page scales
    // to 2560+ via the progressive padding ladder.
    <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
      <p className="text-[12.5px] text-[#8A98AC] mb-2">Last Minute / Requests</p>
      <h1 className="flex items-center gap-2 text-[24px] font-bold text-[#14263D] mb-1">
        Opportunity requests
        <ScoutInfoDot
          id="last-minute-requests"
          title="Opportunity requests"
          body="Colleagues who saw one of your (or an agent-visible) listings can bring you an opportunity here. Accept to open a chat, or ignore it. An ignored request is silent, the sender only ever sees it as pending."
        />
      </h1>
      <p className="text-[13.5px] text-[#5A6B82] mb-5">Requests other agents sent you about a Last Minute listing.</p>

      {loading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-40 rounded-2xl bg-[#F1F4F8] animate-pulse" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-[#E4E9F1]">
          <p className="text-[15px] font-semibold text-[#14263D]">No requests yet</p>
          <p className="text-[13px] text-[#8A98AC] mt-1">When a colleague brings you an opportunity, it appears here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <RequestCard key={r.id} request={r} onAccept={onAccept} onIgnore={onIgnore} busy={busyId === r.id} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReceivedRequestsPage;
